"use client";

import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Folder,
  ShieldCheck,
  Tag,
  Truck,
  Wrench,
} from "lucide-react";
import type { Swiper as SwiperClass } from "swiper";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { A11y, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/a11y";

import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  EcommerceProductsProvider,
  ProductPrice,
  useEcommerceProducts,
  ProductVariationSelector,
  isVariableProduct,
  fetchProductVariations,
  findMatchingVariation,
  getDefaultAttributeSelection,
  type EcommerceProduct,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPQueryParams?: () => Record<string, unknown>;
    };
  }
}

const INFLATABLE_MODELS: Record<number, string> = {
  320: "Starter Inflatable Package",
  321: "Standard Inflatable Package",
  319: "Commercial Inflatable",
  325: "Classic Inflatable",
  327: "EventPro Inflatable",
  328: "VenuePro Inflatable",
  326: "ProSeries Inflatable",
  329: "Professional Inflatable",
  324: "Premium Inflatable",
  330: "Elite Inflatable",
};

function GalleryNavButtons() {
  const swiper = useSwiper();
  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Previous image"
        onClick={() => swiper.slidePrev()}
        className="absolute left-3 top-1/2 z-10 -translate-y-1/2 border border-border bg-card/85 text-foreground hover:bg-card hover:text-foreground shadow-xs"
      >
        <ChevronLeft className="size-5" />
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Next image"
        onClick={() => swiper.slideNext()}
        className="absolute right-3 top-1/2 z-10 -translate-y-1/2 border border-border bg-card/85 text-foreground hover:bg-card hover:text-foreground shadow-xs"
      >
        <ChevronRight className="size-5" />
      </Button>
    </>
  );
}

function ProductDetailConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();
  const product = products && products.length > 0 ? products[0] : null;

  const [activeIndex, setActiveIndex] = useState(0);
  const [mainSwiper, setMainSwiper] = useState<SwiperClass | null>(null);
  const [selection, setSelection] = useState<Record<string, string>>(() =>
    product ? getDefaultAttributeSelection(product) : {}
  );
  const [resolvedVariation, setResolvedVariation] = useState<EcommerceProduct | null>(null);
  const [resolutionStatus, setResolutionStatus] = useState<
    "idle" | "loading" | "ready" | "out_of_stock" | "no_match"
  >("idle");

  useEffect(() => {
    if (!product) return;
    setSelection(getDefaultAttributeSelection(product));
  }, [product?.id]);

  useEffect(() => {
    if (!product) {
      setResolvedVariation(null);
      setResolutionStatus("idle");
      return;
    }

    if (!isVariableProduct(product)) {
      setResolvedVariation(null);
      setResolutionStatus("idle");
      return;
    }

    const attributes = product.attributes || [];
    const isComplete =
      attributes.length > 0 &&
      attributes.every((attr) => Boolean(selection[attr.taxonomy || attr.name]));

    if (!isComplete) {
      setResolvedVariation(null);
      setResolutionStatus("idle");
      return;
    }

    let cancelled = false;
    setResolutionStatus("loading");

    fetchProductVariations(product)
      .then((variations) => {
        if (cancelled) return;
        const match = findMatchingVariation(variations, product, selection);
        if (!match) {
          setResolvedVariation(null);
          setResolutionStatus("no_match");
        } else {
          const inStock =
            match.is_in_stock !== undefined
              ? Boolean(match.is_in_stock)
              : (match as any).stock_status !== "outofstock";
          setResolvedVariation(match);
          setResolutionStatus(inStock ? "ready" : "out_of_stock");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedVariation(null);
        setResolutionStatus("no_match");
      });

    return () => {
      cancelled = true;
    };
  }, [product, selection]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-7">
            <div className="aspect-4/3 w-full animate-pulse bg-muted" />
            <div className="flex gap-3">
              {[0, 1, 2, 3].map((_, i) => (
                <div
                  key={i}
                  data-index={i}
                  className="h-20 w-20 animate-pulse bg-muted"
                />
              ))}
            </div>
          </div>
          <div className="space-y-6 lg:col-span-5">
            <div className="h-8 w-3/4 animate-pulse bg-muted" />
            <div className="h-6 w-1/3 animate-pulse bg-muted" />
            <div className="h-24 w-full animate-pulse bg-muted" />
            <div className="h-12 w-full animate-pulse bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !products || products.length === 0 || !product) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="mx-auto max-w-md border border-border bg-card p-8">
          <h2 className="text-xl font-bold uppercase tracking-wide text-card-foreground">
            Product not found
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The requested equipment unit or component could not be loaded.
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
            <Button asChild variant="default">
              <Link to="/ShopPage">Browse inventory</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const images = product.gallery_image_urls || (product.featured_image_url ? [product.featured_image_url] : []);

  const isPartOrAccessory = product.categories?.some((cat) => {
    const name = cat.name.toLowerCase();
    return name.includes("part") || name.includes("accessor");
  });
  const label = isPartOrAccessory
    ? "Enquire About This Product"
    : "Enquire About This Bull";

  const isInflatableCategory = product.categories?.some(
    (cat) => cat.name?.trim().toLowerCase() === "inflatable mechanical bulls"
  );
  const inflatableModel = INFLATABLE_MODELS[Number(product.id)];
  const showInflatableSpecs = isInflatableCategory && Boolean(inflatableModel);

  const activeStockProduct = resolvedVariation ?? product;
  const isAvailable =
    activeStockProduct.is_in_stock !== undefined
      ? Boolean(activeStockProduct.is_in_stock)
      : (activeStockProduct as any).stock_status !== "outofstock";
  const availabilityText = isAvailable ? "In Stock" : "Out of Stock";

  return (
    <div className="container mx-auto px-4 py-10 lg:py-14">
      {/* Category Navigation Bar */}
      <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Link to="/HomePage" className="transition-colors hover:text-foreground">
          Home
        </Link>
        <span>/</span>
        <Link to="/ShopPage" className="transition-colors hover:text-foreground">
          Equipment
        </Link>
        {product.categories && product.categories.length > 0 && (
          <>
            <span>/</span>
            <span className="text-foreground">{product.categories[0].name}</span>
          </>
        )}
      </nav>

      <article className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
        {/* Gallery Column */}
        <div className="lg:col-span-7">
          <div className="space-y-4">
            {images.length === 0 ? (
              <div className="flex aspect-4/3 items-center justify-center border border-border bg-muted text-muted-foreground">
                <span className="text-sm font-medium uppercase tracking-wide">Image coming soon</span>
              </div>
            ) : images.length === 1 ? (
              <div className="relative aspect-4/3 overflow-hidden border border-border bg-muted">
                <img
                  src={images[0]}
                  alt={product.title}
                  className="h-full w-full object-contain object-center p-6"
                  loading="lazy"
                />
                {product.on_sale && (
                  <div className="absolute top-3 left-3 z-10">
                    <Badge variant="destructive" className="uppercase tracking-wider">
                      Sale
                    </Badge>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="relative aspect-4/3 overflow-hidden border border-border bg-muted">
                  {product.on_sale && (
                    <div className="absolute top-3 left-3 z-20">
                      <Badge variant="destructive" className="uppercase tracking-wider">
                        Sale
                      </Badge>
                    </div>
                  )}
                  <Swiper
                    modules={[Navigation, A11y]}
                    slidesPerView={1}
                    onSwiper={setMainSwiper}
                    onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                    a11y={{ prevSlideMessage: "Previous image", nextSlideMessage: "Next image" }}
                    className="h-full w-full"
                  >
                    {images.map((imgUrl, i) => (
                      <SwiperSlide key={imgUrl + i} data-index={i} className="h-full w-full">
                        <img
                          src={imgUrl}
                          alt={`${product.title} - View ${i + 1}`}
                          className="h-full w-full object-contain object-center p-6"
                          loading={i === 0 ? "eager" : "lazy"}
                        />
                      </SwiperSlide>
                    ))}
                    <GalleryNavButtons />
                  </Swiper>
                </div>

                {/* Thumbnail Row */}
                <div
                  className="flex gap-3 overflow-x-auto pb-1 sm:flex-wrap"
                  role="group"
                  aria-label="Product thumbnail gallery"
                >
                  {images.map((imgUrl, i) => {
                    const isCurrent = i === activeIndex;
                    return (
                      <div key={imgUrl + i} data-index={i} className="shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setActiveIndex(i);
                            mainSwiper?.slideTo(i);
                          }}
                          aria-label={`View image ${i + 1} of ${product.title}`}
                          aria-current={isCurrent ? "true" : undefined}
                          className={`h-20 w-20 p-0 border border-border overflow-hidden rounded-md transition-all ${
                            isCurrent
                              ? "ring-2 ring-primary ring-offset-1 opacity-100"
                              : "opacity-70 hover:opacity-100"
                          }`}
                        >
                          <img
                            src={imgUrl}
                            alt=""
                            aria-hidden="true"
                            className="h-full w-full object-contain object-center p-1"
                            loading="lazy"
                          />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Industrial Specs Summary */}
          <div className="mt-8 border border-border bg-muted/40 p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Equipment assurance
            </h3>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <h4 className="text-xs font-semibold uppercase text-foreground">Commercial grade</h4>
                  <p className="text-xs text-muted-foreground">Heavy-duty construction for commercial venues.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Truck className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <h4 className="text-xs font-semibold uppercase text-foreground">Freight shipping</h4>
                  <p className="text-xs text-muted-foreground">Direct freight logistics across the continental US.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Wrench className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <h4 className="text-xs font-semibold uppercase text-foreground">Technical support</h4>
                  <p className="text-xs text-muted-foreground">Wiring, parts, and assembly consultation provided.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Details & Cart Column */}
        <div className="space-y-6 lg:col-span-5">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {product.title}
            </h1>
            <div className="mt-4 space-y-4">
              {isVariableProduct(product) ? (
                <>
                  <ProductVariationSelector
                    product={product}
                    selection={selection}
                    onChange={setSelection}
                  />
                  <div className="flex flex-wrap items-baseline gap-3">
                    <ProductPrice
                      product={resolvedVariation ?? product}
                      as="div"
                      className="text-2xl font-bold text-foreground"
                    />
                    <span
                      className={`text-sm font-semibold uppercase tracking-wider ${
                        isAvailable
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-destructive"
                      }`}
                    >
                      {availabilityText}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap items-baseline gap-3">
                  <ProductPrice
                    product={product}
                    as="div"
                    className="text-2xl font-bold text-foreground"
                  />
                  <span
                    className={`text-sm font-semibold uppercase tracking-wider ${
                      isAvailable
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-destructive"
                    }`}
                  >
                    {availabilityText}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Inflatable Bulls Compact Spec Plate */}
          {showInflatableSpecs && (
            <div className="border border-border bg-muted/30 p-3">
              <div className="grid grid-cols-3 gap-2 divide-x divide-border">
                <div className="px-2 first:pl-0">
                  <span className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Condition
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    New
                  </span>
                </div>
                <div className="px-2">
                  <span className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Availability
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {availabilityText}
                  </span>
                </div>
                <div className="px-2">
                  <span className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Model
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {inflatableModel}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Short Description */}
          {product.short_description && (
            <div
              className="prose prose-sm max-w-none text-muted-foreground [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          {/* Enquiry / Request Quote Action */}
          <div className="border-y border-border py-6">
            <Button
              asChild
              className="w-full h-auto bg-primary text-primary-foreground font-semibold uppercase tracking-wider py-3"
            >
              <Link to="/RequestQuotePage">
                {label}
              </Link>
            </Button>
          </div>

          {/* Long Description */}
          {product.long_description && (
            <div className="space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">
                Technical overview & description
              </h2>
              <div
                className="prose prose-sm max-w-none text-muted-foreground leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: product.long_description }}
              />
            </div>
          )}

          {/* Product Meta: Categories & Tags */}
          <div className="space-y-3 border-t border-border pt-6 text-xs">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-center gap-2">
                <Folder className="size-4 text-muted-foreground" />
                <span className="font-semibold uppercase tracking-wider text-foreground">Categories:</span>
                <div className="flex flex-wrap gap-1.5">
                  {product.categories.map((cat, i) => (
                    <span key={cat.id || i} data-index={i} className="text-muted-foreground">
                      {cat.name}
                      {i < product.categories.length - 1 ? "," : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex items-center gap-2">
                <Tag className="size-4 text-muted-foreground" />
                <span className="font-semibold uppercase tracking-wider text-foreground">Tags:</span>
                <div className="flex flex-wrap gap-1.5">
                  {product.tags.map((tg, i) => (
                    <Badge
                      key={tg.id || i}
                      data-index={i}
                      variant="outline"
                      className="text-[11px] font-normal uppercase"
                    >
                      {tg.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-2">
              <p className="text-muted-foreground">
                Have questions regarding commercial installation or freight delivery?{" "}
                <Link
                  to="/ContactPage"
                  className="font-medium text-foreground underline hover:text-primary"
                >
                  Contact technical support
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

export default function Product() {
  const queryParams =
    typeof window !== "undefined" && window.wvcClient?.getCurrentWPQueryParams
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  return (
    <section
      id="single-product-template-section"
      data-nav="light"
      className="bg-background text-foreground"
    >
      <EcommerceProductsProvider wp_query={queryParams}>
        <ProductDetailConsumer />
      </EcommerceProductsProvider>
    </section>
  );
}